import {
  CALENDAR_EVENT_ADD_SUCCESS,
  CALENDAR_EVENT_ADD_FAILURE,
  CALENDAR_EVENT_ADD_REQUEST,
  CALENDAR_EVENT_EDIT_SUCCESS,
  CALENDAR_EVENT_EDIT_FAILURE,
  CALENDAR_EVENT_EDIT_REQUEST,
  CALENDAR_EVENT_MULTIPLE_ADD_REQUEST,
  CALENDAR_EVENT_MULTIPLE_ADD_SUCCESS,
  CALENDAR_EVENT_MULTIPLE_ADD_FAILURE,
  FB_CALENDAR_EVENT_GET_REQUEST,
  FB_CALENDAR_EVENT_GET_SUCCESS,
  FB_CALENDAR_EVENT_GET_FAILURE,
  RESET_CALENDAR_STATE,
  SET_CURRENT_EVENT_ID,
  SET_EDITING_EVENT_ID,
  SET_SCROLL_TO_DATE,
  FB_CALENDAR_EVENT_REMOVE_SUCCESS,
  SET_CALENDAR_VIEW_EMPLOYER,
  CALENDAR_SET_SELECTED_CASES,
  CALENDAR_UNAVAILABLE_EVENT_ADD_REQUEST,
  CALENDAR_UNAVAILABLE_EVENT_ADD_SUCCESS,
  CALENDAR_UNAVAILABLE_EVENT_ADD_FAILURE,
  CALENDAR_FETCH_REQUEST,
  CALENDAR_FETCH_SUCCESS,
  CALENDAR_FETCH_FAILURE,
} from '../../actions/actionTypes';
import { EVENTS } from '../../constants/objectTypes';
import { REQUESTING } from '../../actions/statusTypes';
import {
  createEvent as createEventService,
  createEventMultiples as createEventMultiplesService,
  updateEvent as updateEventService,
  getEventById as getEventByIdService,
  deleteEvent as deleteEventService,
  createEventsUnavailableBlocks as createEventsUnavailableBlocksService,
  getCaseEventsAndFiles as getCaseEventsAndFilesService,
} from '../../services/calendarService';
import upsertOperationEnum from '../../constants/eventUpsertOperationEnum';
import {
  uploadFilesAndSaveToServer,
  setUploadStatePendingForObject,
  resetUploadStateForObject,
  removeFileEntryFromReduxAndFirebase,
} from '../components/actions';
import * as sourceTypes from '../../constants/calendarSourceTypes';

import { formatEventObjBeforeSaving } from './event_UI_helpers';
import { queueRequest } from './request_queue_throttle';

import { getCurrentEmployer } from '../../selectors/entities';

/**--------------------- ASYNC ACTIONS ----------------------**/

let fetchingMonth;
export function fetchEventsForMonth(monthDate) {
  return function (dispatch, getState) {
    const employer = getCurrentEmployer(getState());
    if (monthDate === fetchingMonth && getState().screens.calendar.fetchStatus === REQUESTING) {
      return Promise.resolve();
    }
    fetchingMonth = monthDate;

    return dispatch({
      types: [CALENDAR_FETCH_REQUEST, CALENDAR_FETCH_SUCCESS, CALENDAR_FETCH_FAILURE],
      promise: getCaseEventsAndFilesService(employer?.employerId, monthDate),
    });
  };
}

export function setCalendarViewEmployer(val) {
  return { type: SET_CALENDAR_VIEW_EMPLOYER, payload: !!val };
}

export function setCalendarSelectedCases(caseIdArray) {
  return { type: CALENDAR_SET_SELECTED_CASES, payload: caseIdArray || null };
}

export function setEditingEvent(eventId, caseId) {
  return function (dispatch, getState) {
    if (!caseId) caseId = getState().application.currentCaseId;
    dispatch({ type: SET_EDITING_EVENT_ID, payload: { eventId, caseId } });
  };
}

export function clearEventDetails() {
  return function (dispatch, getState) {
    dispatch({ type: SET_CURRENT_EVENT_ID });
    dispatch({ type: SET_EDITING_EVENT_ID });
  };
}

// TODO: look into combining the behavior of setCurrentEvent and setEditingEvent,
// probably dont really need both, but they are serving two different purposes for now
export function setCurrentEvent(currentEventId) {
  return { type: SET_CURRENT_EVENT_ID, payload: currentEventId };
}

export function uploadFiles(eventId, files) {
  return uploadFilesAndSaveToServer(EVENTS, eventId, files);
}

export function setUploadStatePending(eventId) {
  return setUploadStatePendingForObject(EVENTS, eventId);
}

export function clearUploadState(eventId) {
  return resetUploadStateForObject(EVENTS, eventId);
}

export function doneSettingCurrentDate() {
  return { type: SET_SCROLL_TO_DATE };
}

export function createEventForMultipleCases(eventObj, employerId, caseData, patientExtraData) {
  // Prepare object to canonical format. Change repeat and more...
  const formattedEventObj = formatEventObjBeforeSaving(eventObj);

  return {
    types: [
      CALENDAR_EVENT_MULTIPLE_ADD_REQUEST,
      CALENDAR_EVENT_MULTIPLE_ADD_SUCCESS,
      CALENDAR_EVENT_MULTIPLE_ADD_FAILURE,
    ],
    promise: createEventMultiplesService(formattedEventObj, employerId, caseData, patientExtraData),
  };
}

export function createEventsUnavailableBlocks(events, employerId) {
  const formattedEvents = events.map((e) => formatEventObjBeforeSaving(e, e.eventId));

  return {
    types: [
      CALENDAR_UNAVAILABLE_EVENT_ADD_REQUEST,
      CALENDAR_UNAVAILABLE_EVENT_ADD_SUCCESS,
      CALENDAR_UNAVAILABLE_EVENT_ADD_FAILURE,
    ],
    promise: createEventsUnavailableBlocksService(formattedEvents, employerId),
  };
}

export function createEvent(eventObj, sourceObjectId) {
  // Prepare object to canonical format. Change repeat and more...
  const formattedEventObj = formatEventObjBeforeSaving(eventObj);

  return function (dispatch, getState) {
    const entities = getState().entities;
    const savedCase = entities.cases[sourceObjectId];

    dispatch({
      types: [CALENDAR_EVENT_ADD_REQUEST, CALENDAR_EVENT_ADD_SUCCESS, CALENDAR_EVENT_ADD_FAILURE],
      promise: createEventService(formattedEventObj, sourceObjectId, savedCase.name),
    });
  };
}

// export function setEventStateDirty(isDirty) {
//   return function(dispatch, getState) {
//     if (isDirty) dispatch({ type: EVENT_DIRTY });
//     else dispatch({ type: EVENT_CLEAN });
//   };
// }

export function resetState() {
  return { type: RESET_CALENDAR_STATE };
}

/**
 * Action that calls PUT endpoint for event
 * @param eventObj event object
 * @param caseId
 * @param eventId
 * @param operation operation to be done. This is relevant for recurring events, based on this value the server will
 * perform the operation that could be update a single event, future events or all (for old versions).
 * @param exceptionDate
 * @param sourceTypeId
 * @returns {Function}
 */
export function updateEvent(
  eventObj,
  sourceObjectId,
  eventId,
  operation = null,
  exceptionDate = null
) {
  // Prepare object to canonical format. Change repeat and more...
  const formattedEventObj = formatEventObjBeforeSaving(eventObj, eventId);

  return function (dispatch, getState) {
    // If we are editing a recurring event we need to tell the server what we are doing.
    // If nothing is set on the component it default to the simple UPDATE_DEFAULT operation.
    if (!operation) {
      operation = upsertOperationEnum.UPDATE_DEFAULT;
    }
    dispatch({
      types: [
        CALENDAR_EVENT_EDIT_REQUEST,
        CALENDAR_EVENT_EDIT_SUCCESS,
        CALENDAR_EVENT_EDIT_FAILURE,
      ],
      promise: updateEventService(formattedEventObj, sourceObjectId, operation, exceptionDate),
    });
  };
}

export function getCaseEventById(eventId, caseId) {
  return function (dispatch, getState) {
    // queue up the requests to get events by id from the server so we dont
    // block the UI completely if we've got a lot of calendar updates to
    // pull down. Dont throttle requests for the current case so those
    // will appear in the calendar right away. Specifically dont throttle is
    // for when the user creates a new event in the current case while event
    // updates from an employer calendar publish are being pulled down for
    // all of the cases in their app.

    const dontThrottle = caseId === getState().application.currentCaseId;
    // const dontThrottle = true;
    queueRequest(() => {
      dispatch({
        types: [
          FB_CALENDAR_EVENT_GET_REQUEST,
          FB_CALENDAR_EVENT_GET_SUCCESS,
          FB_CALENDAR_EVENT_GET_FAILURE,
        ],
        promise: getEventByIdService(eventId, caseId).then((eventObj) => {
          if (!eventObj.eventId) {
            return Promise.reject('getEventById - Event Not found: ' + eventId);
          }
          return eventObj;
        }),
      });
    }, dontThrottle);
  };
}

export function deleteEvent(eventId, sourceObjectId, operation, exceptionStartDate) {
  return function (dispatch, getState) {
    // If we are deleting a recurring event we need to tell the server what we are doing.
    // If nothing is set on the component it default to the simple DELETE_DEFAULT operation.
    if (!operation) {
      operation = upsertOperationEnum.DELETE_DEFAULT;
    }
    dispatch({
      types: [
        CALENDAR_EVENT_EDIT_REQUEST,
        CALENDAR_EVENT_EDIT_SUCCESS,
        CALENDAR_EVENT_EDIT_FAILURE,
      ],
      promise: deleteEventService(eventId, sourceObjectId, operation, exceptionStartDate)
        .then((result) => {
          console.log('then deleteEvent', result);
          return result;
        })
        .catch((err) => {
          console.log('catch deleteEvent', err);
          if (err.response.data.namespace === 'calendar' && err.response.data.errorCode === 20) {
            // If the event doesn't exist in the server we remove it from redux.
            // We still reject the request to log it, this shouldn't happen.
            dispatch({
              type: FB_CALENDAR_EVENT_REMOVE_SUCCESS,
              payload: eventId,
            });
            return Promise.resolve();
          }
          return Promise.reject(err);
        }),
    });
  };
}

export function removeCaseEventLocally(eventId) {
  return function (dispatch, getState) {
    // If we are deleting the event locally we might get the child_removed event from firebase. If we already removed it
    // Don't do anything here.
    try {
      //Also remove file entries from firebase and redux
      dispatch(removeFileEntryFromReduxAndFirebase(EVENTS, eventId));
      dispatch({ type: FB_CALENDAR_EVENT_REMOVE_SUCCESS, payload: eventId });
    } catch (e) {
      console.error('Error in calendar action removeCaseEventLocally: ' + eventId, e);
    }
  };
}
